import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Panel} from 'react-bootstrap';
import {Link} from 'react-router-dom';
import {Button, Grid} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import {Edit as EditIcon, DeleteForever as DeleteForeverIcon, MoreVert as MoreIcon} from '@material-ui/icons';
import {isEmpty} from 'lodash/lang';
import {BlockUI, CommonModal, DeletionConfirmBox, Modal, DropDownMenu} from 'components/common';
import {SecondaryNavigation} from 'components/Navigation';
import SubscriptionOverview from '../Subscription/SubscriptionOverview';
import UserTable from 'components/User/UserTable';
import ClassroomUpdate from './ClassroomUpdate';
import ClassroomReaderTable, {countWeeklyReadings} from './ClassroomReaderTable';
import TeacherAdditionForm from './TeacherAdditionForm';
import StudentImportForm from './StudentImportForm';
import DirectSubscriptionAssignForm from './DirectSubscriptionAssignForm';
import MoveStudentsToAnotherClassroomWithReadingsForm from './MoveStudentsToAnotherClassroomWithReadingsForm';
import {
  deleteClassroom,
  addUsersToClassroom,
  removeUserFromClassroom,
  fetchAllClassroomReadersWithStats,
  unarchiveClassroom,
} from 'api';
import {
  fetchClassroom,
  fetchClassroomReaders,
  setCurrentClassroomReaderPage,
  setCurrentClassroomReader,
  clearClassroomReadersAfterPage,
  fetchClassroomTeachers,
  setCurrentClassroomTeacherPage,
  setCurrentClassroomTeacher,
  clearClassroomTeachersAfterPage,
} from 'actions/classrooms';
import {addFlashMessage} from 'actions/flash-message';
import {formatErrors} from 'actions/errors';
import {levelMap, generateCSVFile} from 'utils/common';
import {isTeacher, isKluwellAdmin, isSchoolCoordinator, isSchoolCoordinatorOrTeacher} from 'utils/permissions';
import ArchivedStatus from '../common/ArchivedStatus';

class ClassroomDetail extends React.Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };
  static propTypes = {
    classroom: PropTypes.object.isRequired,
    classroomReaderPages: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      blockUIShown: false,
      updateModalShown: false,
      readerTableKey: 0,
      addTeachersModalShown: false,
      importStudentsModalShown: false,
      directSubscriptionsAssignModalShown: false,
      moveStudentsWithReadingsModalShown: false,
      teacherTableKey: 0,
      classroomReaderTableKey: 0,
    };
    this.fetchClassroom = this.fetchClassroom.bind(this);
    this.showModal = this.showModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.addTeachersSuccess = this.addTeachersSuccess.bind(this);
    this.importReadersSuccess = this.importReadersSuccess.bind(this);
    this.removeUserFromClassroom = this.removeUserFromClassroom.bind(this);
    this.deleteClassroom = this.deleteClassroom.bind(this);
    this.downloadReadersInfo = this.downloadReadersInfo.bind(this);
  }

  componentDidMount() {
    this.fetchClassroom();
  }

  fetchClassroom() {
    const {
      fetchClassroom,
      formatErrors,
      match: {
        params: {classroomId},
      },
    } = this.props;
    fetchClassroom(classroomId).catch((err) => formatErrors(err, '/classrooms'));
  }

  showModal(type) {
    this.setState({
      [`${type}ModalShown`]: true,
    });
  }

  closeModal(type) {
    this.setState({
      [`${type}ModalShown`]: false,
    });
  }

  addTeachersSuccess({teachers}) {
    const {
      addFlashMessage,
      formatErrors,
      match: {
        params: {classroomId},
      },
    } = this.props;
    addUsersToClassroom({
      userIds: teachers.map((teacher) => teacher.id),
      classroomId,
    })
      .then(() => {
        this.setState((prevState) => ({
          teacherTableKey: prevState.teacherTableKey + 1,
          addTeachersModalShown: false,
        }));
        addFlashMessage(
          `The teacher${teachers.length > 1 ? 's have' : ' has'} been successfully added to the classroom`,
          'success',
        );
      })
      .catch((err) => formatErrors(err));
  }

  importReadersSuccess() {
    this.fetchClassroom();
    this.setState((prevState) => ({
      importStudentsModalShown: false,
      classroomReaderTableKey: prevState.classroomReaderTableKey + 1,
    }));
  }

  removeUserFromClassroom(user) {
    const {
      addFlashMessage,
      formatErrors,
      match: {
        params: {classroomId},
      },
    } = this.props;
    removeUserFromClassroom(classroomId, user.id)
      .then(() => {
        this.setState((prevState) => ({
          teacherTableKey: prevState.teacherTableKey + 1,
        }));
        addFlashMessage('The user has been successfully removed from the classroom!', 'success');
      })
      .catch((err) => formatErrors(err));
  }

  deleteClassroom() {
    const {
      addFlashMessage,
      formatErrors,
      match: {
        params: {classroomId},
      },
    } = this.props;
    deleteClassroom(classroomId)
      .then(() => {
        addFlashMessage('The classroom has been successfully deleted!', 'success');
        this.context.router.history.push('/classrooms');
      })
      .catch((err) => formatErrors(err));
  }

  downloadReadersInfo() {
    const {classroom, formatErrors} = this.props;
    this.setState({
      blockUIShown: true,
    });
    return fetchAllClassroomReadersWithStats(classroom.id)
      .then(({records}) => {
        generateCSVFile(
          [
            ['First Name', 'Last Name', 'Username', 'Password', 'YTD Readings', 'Weekly Readings'],
            ...records.map((reader) => [
              reader.firstName,
              reader.lastName,
              reader.username,
              reader.rawPassword,
              reader.yearlyReadings,
              countWeeklyReadings(reader.recentReadings),
            ]),
          ],
          `${classroom.name} Students.csv`,
        );
        this.setState({
          blockUIShown: false,
        });
      })
      .catch((err) =>
        formatErrors(err).then(() => {
          this.setState({
            blockUIShown: false,
          });
        }),
      );
  }

  render() {
    const {
      blockUIShown,
      updateModalShown,
      addTeachersModalShown,
      importStudentsModalShown,
      directSubscriptionsAssignModalShown,
      moveStudentsWithReadingsModalShown,
      teacherTableKey,
      classroomReaderTableKey,
    } = this.state;
    const {
      me,
      classroom,
      classroomTeacherPages,
      fetchClassroomTeachers,
      setCurrentClassroomTeacherPage,
      setCurrentClassroomTeacher,
      clearClassroomTeachersAfterPage,
      classroomReaderPages,
      fetchClassroomReaders,
      setCurrentClassroomReaderPage,
      setCurrentClassroomReader,
      clearClassroomReadersAfterPage,
      match: {
        params: {classroomId},
      },
    } = this.props;
    const closeUpdateModal = () => this.closeModal('update');
    return (
      <React.Fragment>
        <BlockUI shown={blockUIShown} />
        <Modal
          title="Directly Assign New Subscriptions to Current Students in the Classroom"
          onClose={() => this.closeModal('directSubscriptionsAssign')}
          open={directSubscriptionsAssignModalShown}
          maxWidth="md"
          renderContent={() => (
            <DirectSubscriptionAssignForm
              schoolId={classroom.schoolId}
              classroomId={classroom.id}
              onSubmitSuccess={() => {
                this.fetchClassroom();
                this.closeModal('directSubscriptionsAssign');
              }}
            />
          )}
        />
        <Modal
          title="Move Current Students to Another Classroom"
          onClose={() => this.closeModal('moveStudentsWithReadings')}
          open={moveStudentsWithReadingsModalShown}
          maxWidth="md"
          renderContent={() => (
            <MoveStudentsToAnotherClassroomWithReadingsForm
              schoolId={classroom.schoolId}
              classroomId={classroom.id}
              onSubmitSuccess={() => {
                this.fetchClassroom();
                this.closeModal('moveStudentsWithReadings');
                this.setState((prevState) => ({
                  classroomReaderTableKey: prevState.classroomReaderTableKey + 1,
                }));
              }}
            />
          )}
        />
        <CommonModal
          title="Import Students from Another Classroom"
          onClose={() => this.closeModal('importStudents')}
          show={importStudentsModalShown}
        >
          <StudentImportForm
            schoolId={classroom.schoolId}
            classroomId={classroom.id}
            onSubmitSuccess={this.importReadersSuccess}
          />
        </CommonModal>
        <SecondaryNavigation
          tabs={[
            {url: `/classrooms/${classroomId}`, label: 'Classroom Detail', isActive: true},
            {
              url: `/classrooms/${classroomId}/bulk-create-students`,
              label: 'Bulk Create Students',
              isActive: false,
            },
            ['kluwell_admin', 'teacher', 'school_coordinator'].includes(me.role) && {
              url: `/classrooms/${classroomId}/admin`,
              label: 'Student Reading Nights',
              isActive: false,
            },
          ].filter((v) => v)}
          hasBack
        />
        <div className="container content-box">
          <Panel>
            <CommonModal title="Edit Classroom" onClose={closeUpdateModal} show={updateModalShown}>
              <ClassroomUpdate classroom={classroom} onSubmitSuccess={closeUpdateModal} />
            </CommonModal>
            <h3 className="section-header">
              <div className="section-title">Classroom Information</div>
              <div className="flex-row-center">
                {(isKluwellAdmin(me.role) || isSchoolCoordinator(me.role)) && (
                  <Button variant="contained" color="primary" onClick={() => this.showModal('importStudents')}>
                    Import Students
                  </Button>
                )}
                <DeletionConfirmBox
                  itemName="classroom"
                  deleteAction={this.deleteClassroom}
                  DeleteButton={(props) => (
                    <IconButton variant="contained" color="primary" {...props}>
                      <DeleteForeverIcon />
                    </IconButton>
                  )}
                />
                <IconButton variant="contained" color="primary" onClick={() => this.showModal('update')}>
                  <EditIcon />
                </IconButton>
              </div>
            </h3>
            <Grid container spacing={1} justify="space-between">
              <Grid item>
                <div className="table-block">
                  <div className="table-row">
                    <div className="table-cell">Name</div>
                    <div className="table-cell">{classroom.name}</div>
                  </div>
                  <div className="table-row">
                    <div className="table-cell">School</div>
                    <div className="table-cell">
                      <Link to={`/schools/${classroom.schoolId}`}>
                        {isEmpty(classroom.school) ? '' : classroom.school.name}
                      </Link>
                    </div>
                  </div>
                  <div className="table-row">
                    <div className="table-cell">App Level</div>
                    <div className="table-cell text-capitalize">{levelMap[classroom.level]}</div>
                  </div>
                  <div className="table-row">
                    <div className="table-cell">Status</div>
                    <div className="table-cell">
                      {!isEmpty(classroom) && <ArchivedStatus isActive={classroom.isActive} />}
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid item>
                {!classroom.isActive && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      unarchiveClassroom(classroomId)
                        .then(() => this.fetchClassroom())
                        .catch(formatErrors)
                    }
                  >
                    Unarchive Classroom
                  </Button>
                )}
              </Grid>
            </Grid>
          </Panel>

          <Panel>
            <h3 className="section-header">
              <div className="section-title">Students</div>
              <Grid container spacing={1} alignItems="center" justify="flex-end">
                <DropDownMenu
                  buttonProps={{variant: 'contained', color: 'primary'}}
                  buttonText={<MoreIcon />}
                  items={
                    isKluwellAdmin(me.role) || isSchoolCoordinator(me.role)
                      ? [
                          {value: 'directSubscriptionsAssign', label: 'Assign New Subscriptions to Current Students'},
                          {value: 'moveStudentsWithReadings', label: 'Move Current Students to Another Classroom'},
                          {value: 'download', label: 'Download Students Information'},
                        ]
                      : [{value: 'download', label: 'Download Students Information'}]
                  }
                  onSelect={(value) => {
                    if (value === 'directSubscriptionsAssign') {
                      this.showModal('directSubscriptionsAssign');
                    } else if (value === 'moveStudentsWithReadings') {
                      this.showModal('moveStudentsWithReadings');
                    } else if (value === 'download') {
                      this.downloadReadersInfo();
                    }
                  }}
                  hasCaret={false}
                />
              </Grid>
            </h3>
            <ClassroomReaderTable
              {...{
                key: classroomReaderTableKey,
                recordPages: classroomReaderPages,
                fetchRecords: (...args) => fetchClassroomReaders(classroomId, ...args),
                setCurrentRecordPage: setCurrentClassroomReaderPage,
                setCurrentRecord: setCurrentClassroomReader,
                clearRecordsAfterPage: clearClassroomReadersAfterPage,
              }}
            />
          </Panel>

          {!isEmpty(classroom) && (
            <Panel>
              <SubscriptionOverview
                urlPrefix={`/classrooms/${classroomId}`}
                activatedSubscriptions={classroom.activatedSubscriptions}
                pendingSubscriptions={classroom.pendingSubscriptions}
                availableSubscriptions={classroom.availableSubscriptions}
                ofClassroom
              />
            </Panel>
          )}

          <Panel>
            <CommonModal
              title="Add Teachers to Classroom"
              onClose={() => this.closeModal('addTeachers')}
              show={addTeachersModalShown}
            >
              <TeacherAdditionForm schoolId={classroom.schoolId} onSubmit={this.addTeachersSuccess} />
            </CommonModal>
            <h3 className="section-header">
              <div className="section-title">Teachers</div>
              <Button variant="contained" color="primary" onClick={() => this.showModal('addTeachers')}>
                Add Teachers
              </Button>
            </h3>
            <UserTable
              {...{
                refreshTableKey: teacherTableKey,
                recordPages: classroomTeacherPages,
                fetchRecords: (...args) => fetchClassroomTeachers(classroomId, ...args),
                setCurrentRecordPage: setCurrentClassroomTeacherPage,
                setCurrentRecord: setCurrentClassroomTeacher,
                clearRecordsAfterPage: clearClassroomTeachersAfterPage,
                hiddenFields: ['role'],
                onDeleteActionClick: this.removeUserFromClassroom,
                me,
              }}
            />
          </Panel>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  const {account, classrooms} = state;
  return {
    me: account.get('user').toJS(),
    classroom: classrooms.get('currentClassroom').toJS(),
    classroomReaderPages: classrooms.get('classroomReaderPages').toJS(),
    classroomTeacherPages: classrooms.get('classroomTeacherPages').toJS(),
  };
};

const mapDispatchToProps = (dispatch) => ({
  addFlashMessage: (...args) => dispatch(addFlashMessage(...args)),
  formatErrors: (...args) => dispatch(formatErrors(...args)),
  fetchClassroom: (...args) => dispatch(fetchClassroom(...args)),
  // Classroom Student
  fetchClassroomReaders: (...args) => dispatch(fetchClassroomReaders(...args)),
  setCurrentClassroomReaderPage: (...args) => dispatch(setCurrentClassroomReaderPage(...args)),
  setCurrentClassroomReader: (...args) => dispatch(setCurrentClassroomReader(...args)),
  clearClassroomReadersAfterPage: (...args) => dispatch(clearClassroomReadersAfterPage(...args)),
  // Classroom Teacher
  fetchClassroomTeachers: (...args) => dispatch(fetchClassroomTeachers(...args)),
  setCurrentClassroomTeacherPage: (...args) => dispatch(setCurrentClassroomTeacherPage(...args)),
  setCurrentClassroomTeacher: (...args) => dispatch(setCurrentClassroomTeacher(...args)),
  clearClassroomTeachersAfterPage: (...args) => dispatch(clearClassroomTeachersAfterPage(...args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClassroomDetail);
